import WeightedAverageInterface from '@app/Interface/WeightedAverageInterface';
import { Alert, Title } from '@patternfly/react-core';
import { TableComposable, Tbody, Td, Tr } from '@patternfly/react-table';
import * as React from 'react';
import moment from 'moment-timezone';
import ItemLoader from '@app/Components/ItemLoader';
import ErrorResponseInterface from '@app/Interface/ErrorResponseInterface';
import WeightedAverageModel from '@app/Model/WeightedAverageModel';

interface MonthToDateProps {
    monthToDate: WeightedAverageInterface[] | ErrorResponseInterface;
    isLoading: boolean;
}

const MonthToDate: React.FC<MonthToDateProps> = ({ monthToDate, isLoading }) => {

    let currentWeightedAverage: WeightedAverageInterface = new WeightedAverageModel();

    let previousWeightedAverage: WeightedAverageInterface = new WeightedAverageModel();

    if (Array.isArray(monthToDate) && monthToDate.length === 2) {
        
        currentWeightedAverage = monthToDate[0];

        previousWeightedAverage = monthToDate[1];
    }

    let displayCurrentMonth = "No Data";

    if (currentWeightedAverage.MonthYear_PK) {

        const currentMonth = moment(currentWeightedAverage.MonthYear_PK);

        displayCurrentMonth = currentMonth.format("MMMM");

    }
    
    let displayPreviousMonth = "No Data";

    if (previousWeightedAverage.MonthYear_PK) {

        const previousMonth = moment(previousWeightedAverage.MonthYear_PK);

        displayPreviousMonth = previousMonth.format("MMMM");

    }
    
    if (isLoading) {

        return (
            <>
                <Title headingLevel={'h2'} className="pf-u-mb-md">Month to Date</Title>
                <ItemLoader />
            </>
        );

    } else {

        return (
            <>
                <Title headingLevel={'h2'} className="pf-u-mb-md">Month to Date</Title>
                {currentWeightedAverage.ProducerID === 0
                    ? <Alert
                        variant="warning"
                        title="No Data"
                        aria-live="polite"
                        role="alert"
                    >
                        <p>No month to date information was found for the producer.</p>
                    </Alert>
                    : <TableComposable
                        aria-label="Month to Date"
                        variant="compact"
                        borders
                        className="pf-u-mb-md"
                        gridBreakPoint=''
                    >
                        <Tbody>
                            <Tr>
                                <Td className="pf-u-font-weight-bold">Month</Td>
                                <Td>{displayCurrentMonth}</Td>
                                <Td>{displayPreviousMonth}</Td>
                            </Tr>
                            <Tr>
                                <Td className="pf-u-font-weight-bold">Production</Td>
                                <Td>{currentWeightedAverage?.Weight ? Number(currentWeightedAverage.Weight).toLocaleString() : "-"} lbs</Td>
                                <Td>{previousWeightedAverage?.Weight ? Number(previousWeightedAverage.Weight).toLocaleString() : "-"} lbs</Td>
                            </Tr>
                            <Tr>
                                <Td className="pf-u-font-weight-bold">Butterfat</Td>
                                <Td>{currentWeightedAverage?.Fat ? Number(currentWeightedAverage.Fat).toFixed(2) : ''}%</Td>
                                <Td>{previousWeightedAverage?.Fat ? Number(previousWeightedAverage.Fat).toFixed(2) : ''}%</Td>
                            </Tr>
                            <Tr>
                                <Td className="pf-u-font-weight-bold">Protein</Td>
                                <Td>{currentWeightedAverage?.Protein ? Number(currentWeightedAverage.Protein).toFixed(2) : ''}%</Td>
                                <Td>{previousWeightedAverage?.Protein ? Number(previousWeightedAverage.Protein).toFixed(2) : ''}%</Td>
                            </Tr>
                            <Tr>
                                <Td className="pf-u-font-weight-bold">Other Solids</Td>
                                <Td>{currentWeightedAverage?.OtherSolids ? Number(currentWeightedAverage.OtherSolids).toFixed(2) : ''}%</Td>
                                <Td>{previousWeightedAverage?.OtherSolids ? Number(previousWeightedAverage.OtherSolids).toFixed(2) : ''}%</Td>
                            </Tr>
                            <Tr>
                                <Td className="pf-u-font-weight-bold">Somatic Cell</Td>
                                <Td>{currentWeightedAverage?.Leuc ?? ''}</Td>
                                <Td>{previousWeightedAverage?.Leuc ?? ''}</Td>
                            </Tr>
                            <Tr>
                                <Td className="pf-u-font-weight-bold">RAW Average</Td>
                                <Td>{currentWeightedAverage?.AvgRaw ?? ''}</Td>
                                <Td>{previousWeightedAverage?.AvgRaw ?? ''}</Td>
                            </Tr>
                            <Tr>
                                <Td className="pf-u-font-weight-bold">PI Average</Td>
                                <Td>{currentWeightedAverage?.AvgPI ?? ''}</Td>
                                <Td>{previousWeightedAverage?.AvgPI ?? ''}</Td>
                            </Tr>
                            <Tr>
                                <Td className="pf-u-font-weight-bold">M.U.N.</Td>
                                <Td>{currentWeightedAverage?.MUN ? Number(currentWeightedAverage.MUN).toFixed(2) : ''}</Td>
                                <Td>{previousWeightedAverage?.MUN ? Number(previousWeightedAverage?.MUN).toFixed(2) : ''}</Td>
                            </Tr>
                        </Tbody>

                    </TableComposable>
                }

            </>
        );

    }

}

export default MonthToDate;